/**
 * Capsule drafts — the list of capsules this creator saved without publishing.
 *
 * Drafts create no post, so a feed can never lead back to them; this dialog is how
 * the creator finds their unfinished work again and continues editing it.
 *
 * A row shows the capsule's *shape* rather than its artwork, and that is the one
 * place this list differs from the FlashBook one: a book is recognised by the
 * picture on its cover, while a capsule's face is its theme and how many slides it
 * has. So a row carries the theme's own colour as a dot — the same dot the studio's
 * theme pill uses, which is what makes a row recognisable to the creator who chose
 * it — the slide count, and how long ago it was saved.
 */
import {View} from 'react-native'
import {type MessageDescriptor} from '@lingui/core'
import {msg, plural} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {type CapsuleDraftRef} from '#/lib/capsuleDrafts'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

export function CapsuleDraftsDialog({
  control,
  drafts,
  openingUri,
  onOpen,
  onRemove,
}: {
  control: Dialog.DialogControlProps
  drafts: CapsuleDraftRef[]
  /** Draft currently being fetched — its row shows a spinner. */
  openingUri: string | null
  onOpen: (draft: CapsuleDraftRef) => void
  onRemove: (draft: CapsuleDraftRef) => void
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      <DialogInner
        drafts={drafts}
        openingUri={openingUri}
        onOpen={onOpen}
        onRemove={onRemove}
      />
    </Dialog.Outer>
  )
}

/** "Saved 2 h ago" without pulling in a date library. */
function savedAgo(iso: string, l: (d: MessageDescriptor) => string) {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (minutes < 1) return l(msg`just now`)
  if (minutes < 60) return l(msg`${minutes} min ago`)
  const hours = Math.round(minutes / 60)
  if (hours < 24) return l(msg`${hours} h ago`)
  return l(msg`${Math.round(hours / 24)} d ago`)
}

/**
 * The theme dot the studio's own theme pill uses.
 *
 * A creator who has two drafts of the same lesson tells them apart by the theme
 * they chose for each, so the row shows the same colour signal the studio does. A
 * draft whose theme names nothing this build knows still gets the dot in the
 * chrome's ink, because "a theme I cannot render" and "no theme" are different
 * things to a creator looking for their work.
 */
function ThemeDot({color}: {color: string}) {
  return (
    <View
      testID="capsuleDraftThemeDot"
      style={{width: 10, height: 10, borderRadius: 5, backgroundColor: color}}
    />
  )
}

function DialogInner({
  drafts,
  openingUri,
  onOpen,
  onRemove,
}: {
  drafts: CapsuleDraftRef[]
  openingUri: string | null
  onOpen: (draft: CapsuleDraftRef) => void
  onRemove: (draft: CapsuleDraftRef) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <Dialog.ScrollableInner
      style={web({maxWidth: 420})}
      label={l`Unpublished drafts`}>
      <View style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>
            <Trans>Unpublished drafts</Trans>
          </Text>
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
            <Trans>
              Only you can see these, on every device you sign in to. Pick one
              up to keep editing, or start a new capsule.
            </Trans>
          </Text>
        </View>

        <View style={[a.gap_sm]}>
          {drafts.map(draft => {
            const busy = openingUri === draft.uri
            const slides = l`${plural(draft.slideCount, {
              one: '# slide',
              other: '# slides',
            })}`
            const saved = savedAgo(draft.updatedAt, l)
            return (
              <View
                key={draft.uri}
                testID={`capsuleDraftRow--${draft.uri}`}
                style={[
                  a.flex_row,
                  a.align_center,
                  a.gap_sm,
                  t.atoms.bg_contrast_25,
                  t.atoms.border_contrast_low,
                  a.border,
                  a.rounded_sm,
                  a.px_md,
                  a.py_sm,
                ]}>
                <ThemeDot color={t.palette.primary_500} />
                <View style={[a.flex_1, a.gap_xs]}>
                  <Text
                    style={[a.text_md, a.font_semi_bold, a.leading_snug]}
                    numberOfLines={1}>
                    {draft.title}
                  </Text>
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    {slides}
                    {saved ? ` · ${saved}` : ''}
                  </Text>
                </View>
                <Button
                  label={l`Continue editing ${draft.title}`}
                  disabled={busy || !!openingUri}
                  onPress={() => onOpen(draft)}
                  size="small"
                  variant="solid"
                  color="secondary"
                  testID={`capsuleDraftOpen--${draft.uri}`}>
                  <ButtonText>
                    <Trans>Continue</Trans>
                  </ButtonText>
                  {/*
                    The wait, on the row that was tapped — the thing a browser can
                    see and a reader of the source cannot.
                  */}
                  {busy ? (
                    <View testID={`capsuleDraftBusy--${draft.uri}`}>
                      <ButtonIcon icon={Loader} />
                    </View>
                  ) : null}
                </Button>
                <Button
                  label={l`Delete draft ${draft.title}`}
                  accessibilityHint={l`Deletes it from your drafts on every device`}
                  disabled={busy || !!openingUri}
                  onPress={() => onRemove(draft)}
                  size="small"
                  variant="ghost"
                  color="secondary"
                  shape="round"
                  testID={`capsuleDraftDelete--${draft.uri}`}>
                  <ButtonIcon icon={X} />
                </Button>
              </View>
            )
          })}
        </View>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}
